<template>
  <div class="seo-cost-page" v-loading="loading">
    <div class="seo-cost-page__header">
      <div>
        <div class="seo-cost-page__title">SEO 消耗统计</div>
        <div class="seo-cost-page__desc">
          DataForSEO 调用费用、站点消耗与周期预算观察
        </div>
      </div>
      <div class="seo-cost-page__actions">
        <el-select
          v-model="params.site_id"
          clearable
          filterable
          size="small"
          placeholder="全部站点"
          class="seo-cost-page__site-select"
        >
          <el-option
            v-for="item in siteOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-select
          v-model="params.call_mode"
          clearable
          size="small"
          placeholder="全部调用方式"
          class="seo-cost-page__mode-select"
        >
          <el-option label="Live手动诊断" value="live_manual"></el-option>
          <el-option
            label="Standard全部站点异步"
            value="standard_all"
          ></el-option>
          <el-option
            label="Standard单站点异步"
            value="standard_site"
          ></el-option>
          <el-option
            label="Standard结果采集"
            value="standard_collect"
          ></el-option>
          <el-option label="连接测试" value="system_connection"></el-option>
          <el-option label="钱包余额查询" value="system_wallet"></el-option>
          <el-option label="关键词拓展" value="expand_keywords"></el-option>
        </el-select>
        <el-select
          v-model="params.status"
          clearable
          size="small"
          placeholder="全部状态"
          class="seo-cost-page__status-select"
        >
          <el-option label="成功" value="success"></el-option>
          <el-option label="失败" value="failed"></el-option>
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="small"
          class="seo-cost-page__date-range"
        ></el-date-picker>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-search"
          @click="onSearch"
        >
          查询
        </el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="onReset">
          重置
        </el-button>
      </div>
    </div>

    <div class="seo-cost-page__scope">
      数据口径：{{ currentSiteLabel }} / {{ currentModeLabel }} /
      {{ currentStatusLabel }} /
      {{ currentDateLabel }}。页面只读取本地消耗统计数据。
    </div>

    <div class="seo-cost-page__stats">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="seo-cost-page__stat-card"
      >
        <div
          :class="[
            'seo-cost-page__stat-icon',
            `seo-cost-page__stat-icon--${card.tone}`,
          ]"
        >
          <i :class="card.icon"></i>
        </div>
        <div class="seo-cost-page__stat-body">
          <div class="seo-cost-page__stat-label">{{ card.label }}</div>
          <div class="seo-cost-page__stat-value">
            {{ formatCost(card.value) }}
          </div>
          <div class="seo-cost-page__stat-extra">{{ card.extra }}</div>
        </div>
      </div>
    </div>

    <div class="seo-cost-page__grid">
      <div class="seo-cost-page__panel seo-cost-page__panel--wide">
        <div class="seo-cost-page__panel-head">
          <div>
            <div class="seo-cost-page__panel-title">站点消耗排行</div>
            <div class="seo-cost-page__panel-note">
              按当前筛选条件聚合站点成本
            </div>
          </div>
          <span class="seo-cost-page__panel-badge">
            {{ siteChartRows.length }} 个站点
          </span>
        </div>
        <div ref="siteCostChart" class="seo-cost-page__chart"></div>
      </div>

      <div class="seo-cost-page__panel">
        <div class="seo-cost-page__panel-head">
          <div>
            <div class="seo-cost-page__panel-title">消耗结构</div>
            <div class="seo-cost-page__panel-note">
              今日、近7天、近30天与长期成本占比
            </div>
          </div>
        </div>
        <div ref="periodChart" class="seo-cost-page__chart"></div>
      </div>
    </div>

    <div class="seo-cost-page__grid seo-cost-page__grid--detail">
      <div class="seo-cost-page__panel">
        <div class="seo-cost-page__panel-head">
          <div>
            <div class="seo-cost-page__panel-title">费用观察</div>
            <div class="seo-cost-page__panel-note">根据当前统计结果生成</div>
          </div>
        </div>
        <div class="seo-cost-page__insights">
          <div
            v-for="item in insightItems"
            :key="item.label"
            class="seo-cost-page__insight"
          >
            <div
              :class="[
                'seo-cost-page__insight-dot',
                `seo-cost-page__insight-dot--${item.tone}`,
              ]"
            ></div>
            <div>
              <div class="seo-cost-page__insight-label">{{ item.label }}</div>
              <div class="seo-cost-page__insight-value">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="seo-cost-page__panel seo-cost-page__panel--table">
        <div class="seo-cost-page__panel-head">
          <div>
            <div class="seo-cost-page__panel-title">站点明细</div>
            <div class="seo-cost-page__panel-note">
              展示站点级 DataForSEO 消耗
            </div>
          </div>
        </div>
        <el-table :data="siteTableRows" height="300">
          <el-table-column label="站点" min-width="180">
            <template v-slot="scope">
              <div class="seo-cost-page__site-name">
                {{ scope.row.site_name || "未关联站点" }}
              </div>
              <div class="seo-cost-page__site-id">
                ID {{ scope.row.site_id || "-" }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="消耗" width="140">
            <template v-slot="scope">
              <span class="seo-cost-page__cost-value">
                {{ formatCost(scope.row.cost) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="占比" min-width="160">
            <template v-slot="scope">
              <el-progress
                :percentage="scope.row.percent"
                :stroke-width="8"
                :show-text="false"
              ></el-progress>
              <span class="seo-cost-page__percent-text">
                {{ scope.row.percent.toFixed(2) }}%
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import { apiSeoCostSummary, apiSeoSiteOptions } from "@/api/seo";
import * as echarts from "echarts/core";
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { BarChart, PieChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  BarChart,
  PieChart,
  CanvasRenderer,
]);

export default {
  data() {
    return {
      loading: false,
      siteOptions: [],
      params: {
        site_id: "",
        call_mode: "",
        status: "",
      },
      dateRange: [],
      summary: {},
      charts: {},
    };
  },
  computed: {
    summaryCards() {
      return [
        {
          key: "total",
          label: "总消耗",
          value: this.summary.total,
          extra: "当前筛选累计成本",
          icon: "el-icon-data-line",
          tone: "blue",
        },
        {
          key: "today",
          label: "今日",
          value: this.summary.today,
          extra: this.todayCostTip,
          icon: "el-icon-date",
          tone: "green",
        },
        {
          key: "days_7",
          label: "近7天",
          value: this.summary.days_7,
          extra: "短周期调用成本",
          icon: "el-icon-timer",
          tone: "yellow",
        },
        {
          key: "days_30",
          label: "近30天",
          value: this.summary.days_30,
          extra: "月度成本观察",
          icon: "el-icon-pie-chart",
          tone: "red",
        },
        {
          key: "days_180",
          label: "近180天",
          value: this.summary.days_180,
          extra: "半年累计成本",
          icon: "el-icon-s-marketing",
          tone: "purple",
        },
        {
          key: "days_365",
          label: "近365天",
          value: this.summary.days_365,
          extra: "年度累计成本",
          icon: "el-icon-odometer",
          tone: "cyan",
        },
      ];
    },
    siteChartRows() {
      return (this.summary.by_site || [])
        .map((item, index) => ({
          key: `${item.site_id || "none"}_${index}`,
          site_id: item.site_id,
          site_name: item.site_name || "未关联站点",
          cost: Number(item.cost || 0),
        }))
        .sort((a, b) => b.cost - a.cost);
    },
    siteTableRows() {
      const total = Number(this.summary.total || 0);
      return this.siteChartRows.map((item) => ({
        ...item,
        percent: total > 0 ? Math.min(100, (item.cost / total) * 100) : 0,
      }));
    },
    periodRows() {
      return [
        { name: "今日", value: Number(this.summary.today || 0) },
        { name: "近7天", value: Number(this.summary.days_7 || 0) },
        { name: "近30天", value: Number(this.summary.days_30 || 0) },
        { name: "近180天", value: Number(this.summary.days_180 || 0) },
        { name: "近365天", value: Number(this.summary.days_365 || 0) },
      ].filter((item) => item.value > 0);
    },
    insightItems() {
      const topSite = this.siteChartRows[0];
      const total = Number(this.summary.total || 0);
      const today = Number(this.summary.today || 0);
      const days7 = Number(this.summary.days_7 || 0);
      const days30 = Number(this.summary.days_30 || 0);
      return [
        {
          label: "最高消耗站点",
          value: topSite
            ? `${topSite.site_name} / ${this.formatCost(topSite.cost)}`
            : "暂无站点消耗",
          tone: "blue",
        },
        {
          label: "今日占总消耗",
          value: total > 0 ? `${((today / total) * 100).toFixed(2)}%` : "0.00%",
          tone: "green",
        },
        {
          label: "近7天日均",
          value: this.formatCost(days7 / 7),
          tone: "yellow",
        },
        {
          label: "近30天日均",
          value: this.formatCost(days30 / 30),
          tone: "red",
        },
      ];
    },
    todayCostTip() {
      const today = Number(this.summary.today || 0);
      return today > 0 ? "今日已有调用成本" : "今日暂无消耗";
    },
    currentSiteLabel() {
      if (!this.params.site_id) {
        return "全部站点";
      }
      const site = this.siteOptions.find(
        (item) => String(item.id) === String(this.params.site_id)
      );
      return site ? site.name : `站点 ${this.params.site_id}`;
    },
    currentModeLabel() {
      const map = {
        live_manual: "Live手动诊断",
        standard_all: "Standard全部站点异步",
        standard_site: "Standard单站点异步",
        standard_collect: "Standard结果采集",
        system_connection: "连接测试",
        system_wallet: "钱包余额查询",
        expand_keywords: "关键词拓展",
      };
      return map[this.params.call_mode] || "全部调用方式";
    },
    currentStatusLabel() {
      const map = {
        success: "成功",
        failed: "失败",
      };
      return map[this.params.status] || "全部状态";
    },
    currentDateLabel() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return "全部时间";
      }
      return `${this.formatDate(this.dateRange[0])} 至 ${this.formatDate(
        this.dateRange[1]
      )}`;
    },
  },
  created() {
    this.getSiteOptions();
    this.getSummary();
  },
  mounted() {
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCharts);
    Object.values(this.charts).forEach((chart) => chart.dispose());
  },
  methods: {
    getSiteOptions() {
      apiSeoSiteOptions().then((resp) => {
        this.siteOptions = resp || [];
      });
    },
    getSummary() {
      this.loading = true;
      apiSeoCostSummary(this.queryParams())
        .then((resp) => {
          this.summary = resp || {};
          this.$nextTick(this.renderCharts);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSearch() {
      this.getSummary();
    },
    onReset() {
      this.params.site_id = "";
      this.params.call_mode = "";
      this.params.status = "";
      this.dateRange = [];
      this.getSummary();
    },
    queryParams() {
      const params = { ...this.params };
      if (this.dateRange && this.dateRange.length === 2) {
        params.start_time = this.toUnix(this.dateRange[0]);
        params.end_time = this.toUnix(this.dateRange[1]) + 86399;
      }
      return params;
    },
    chart(ref) {
      if (!this.$refs[ref]) {
        return null;
      }
      if (!this.charts[ref]) {
        this.charts[ref] = echarts.init(this.$refs[ref]);
      }
      return this.charts[ref];
    },
    renderCharts() {
      this.renderSiteCostChart();
      this.renderPeriodChart();
      this.resizeCharts();
    },
    renderSiteCostChart() {
      const chart = this.chart("siteCostChart");
      if (!chart) {
        return;
      }
      const rows = this.siteChartRows;
      if (!rows.length) {
        chart.setOption(this.emptyChartOption("暂无站点消耗数据"), true);
        return;
      }
      chart.setOption(
        {
          color: ["#4285f4"],
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: (items) => {
              const item = items[0];
              return `${item.name}<br/>消耗：${this.formatCost(item.value)}`;
            },
          },
          grid: {
            top: 24,
            left: 12,
            right: 18,
            bottom: 8,
            containLabel: true,
          },
          xAxis: {
            type: "value",
            axisLabel: {
              color: "#5f6368",
              formatter: (value) => this.formatCost(value),
            },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: "#edf0f5" } },
          },
          yAxis: {
            type: "category",
            inverse: true,
            data: rows.map((item) => item.site_name),
            axisLabel: {
              color: "#3c4043",
              width: 118,
              overflow: "truncate",
            },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          series: [
            {
              name: "消耗",
              type: "bar",
              data: rows.map((item) => item.cost),
              barWidth: 18,
              itemStyle: {
                borderRadius: [0, 8, 8, 0],
                color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: "#8ab4f8" },
                  { offset: 1, color: "#1a73e8" },
                ]),
              },
            },
          ],
        },
        true
      );
    },
    renderPeriodChart() {
      const chart = this.chart("periodChart");
      if (!chart) {
        return;
      }
      if (!this.periodRows.length) {
        chart.setOption(this.emptyChartOption("暂无周期消耗数据"), true);
        return;
      }
      chart.setOption(
        {
          color: ["#1a73e8", "#34a853", "#fbbc04", "#ea4335", "#9334e6"],
          tooltip: {
            trigger: "item",
            formatter: (item) =>
              `${item.name}<br/>消耗：${this.formatCost(item.value)} (${
                item.percent
              }%)`,
          },
          legend: {
            bottom: 0,
            icon: "circle",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { color: "#5f6368" },
          },
          series: [
            {
              name: "周期消耗",
              type: "pie",
              radius: ["48%", "72%"],
              center: ["50%", "44%"],
              avoidLabelOverlap: true,
              itemStyle: {
                borderColor: "#fff",
                borderWidth: 3,
              },
              label: {
                color: "#3c4043",
                formatter: (item) =>
                  `${item.name}\n${this.formatCost(item.value)}`,
              },
              data: this.periodRows,
            },
          ],
        },
        true
      );
    },
    emptyChartOption(text) {
      return {
        graphic: {
          type: "text",
          left: "center",
          top: "middle",
          style: {
            text,
            fill: "#9aa0a6",
            fontSize: 13,
          },
        },
      };
    },
    resizeCharts() {
      Object.values(this.charts).forEach((chart) => chart.resize());
    },
    toUnix(value) {
      return Math.floor(new Date(value).getTime() / 1000);
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatCost(value) {
      return Number(value || 0).toFixed(6);
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-cost-page {
  min-height: calc(100vh - 84px);
  margin: -20px;
  padding: 24px;
  background: #f8fafc;
  color: #202124;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 20px 22px;
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;
  }

  &__desc {
    margin-top: 4px;
    color: #5f6368;
    font-size: 13px;
    line-height: 20px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
    max-width: 920px;
  }

  &__site-select {
    width: 190px;
  }

  &__mode-select {
    width: 190px;
  }

  &__status-select {
    width: 130px;
  }

  &__date-range {
    width: 250px;
  }

  &__scope {
    margin-top: 12px;
    padding: 10px 14px;
    color: #5f6368;
    font-size: 13px;
    line-height: 20px;
    background: #eef4ff;
    border: 1px solid #d9e7ff;
    border-radius: 8px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px;
    margin-top: 16px;
  }

  &__stat-card {
    display: flex;
    gap: 12px;
    min-height: 112px;
    padding: 16px;
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__stat-icon {
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 8px;
    font-size: 18px;

    &--blue {
      background: #1a73e8;
    }

    &--green {
      background: #34a853;
    }

    &--yellow {
      background: #fbbc04;
      color: #202124;
    }

    &--red {
      background: #ea4335;
    }

    &--purple {
      background: #9334e6;
    }

    &--cyan {
      background: #12b5cb;
    }
  }

  &__stat-body {
    min-width: 0;
  }

  &__stat-label {
    color: #5f6368;
    font-size: 13px;
    line-height: 18px;
  }

  &__stat-value {
    margin-top: 6px;
    color: #202124;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    word-break: break-all;
  }

  &__stat-extra {
    margin-top: 4px;
    color: #80868b;
    font-size: 12px;
    line-height: 18px;
  }

  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.8fr);
    gap: 16px;
    margin-top: 16px;

    &--detail {
      grid-template-columns: minmax(320px, 0.75fr) minmax(0, 1.35fr);
    }
  }

  &__panel {
    min-width: 0;
    padding: 18px;
    background: #fff;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.08);
  }

  &__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__panel-title {
    color: #202124;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__panel-note {
    margin-top: 2px;
    color: #80868b;
    font-size: 12px;
    line-height: 18px;
  }

  &__panel-badge {
    flex: 0 0 auto;
    padding: 3px 9px;
    color: #1a73e8;
    font-size: 12px;
    line-height: 18px;
    background: #e8f0fe;
    border-radius: 999px;
  }

  &__chart {
    width: 100%;
    height: 330px;
  }

  &__insights {
    display: grid;
    gap: 12px;
  }

  &__insight {
    display: flex;
    gap: 10px;
    min-height: 56px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #edf0f5;
    border-radius: 8px;
  }

  &__insight-dot {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    margin-top: 4px;
    border-radius: 999px;

    &--blue {
      background: #1a73e8;
    }

    &--green {
      background: #34a853;
    }

    &--yellow {
      background: #fbbc04;
    }

    &--red {
      background: #ea4335;
    }
  }

  &__insight-label {
    color: #5f6368;
    font-size: 12px;
    line-height: 18px;
  }

  &__insight-value {
    margin-top: 2px;
    color: #202124;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    word-break: break-word;
  }

  &__site-name {
    color: #202124;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
  }

  &__site-id {
    margin-top: 2px;
    color: #9aa0a6;
    font-size: 12px;
    line-height: 16px;
  }

  &__cost-value {
    color: #202124;
    font-weight: 700;
  }

  &__percent-text {
    display: inline-block;
    margin-top: 5px;
    color: #5f6368;
    font-size: 12px;
  }

  ::v-deep .el-table {
    color: #3c4043;
  }

  ::v-deep .el-table th.el-table__cell {
    background: #f8fafc;
    color: #5f6368;
    font-weight: 600;
  }

  ::v-deep .el-progress-bar__outer {
    background: #edf0f5;
  }
}

@media (max-width: 1440px) {
  .seo-cost-page {
    &__stats {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

@media (max-width: 1100px) {
  .seo-cost-page {
    &__header {
      flex-direction: column;
    }

    &__actions {
      justify-content: flex-start;
      max-width: none;
    }

    &__grid,
    &__grid--detail {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 760px) {
  .seo-cost-page {
    margin: -12px;
    padding: 12px;

    &__stats {
      grid-template-columns: 1fr;
    }

    &__site-select,
    &__mode-select,
    &__status-select,
    &__date-range {
      width: 100%;
    }

    &__actions {
      width: 100%;
    }

    &__chart {
      height: 300px;
    }
  }
}
</style>
